import React, { useEffect, useState } from "react";
import { updateApplicant } from "../api";
import "../styles/ScoreEditor.css";

const STORAGE_PREFIX = "scoreEditor:";

export default function ScoreEditor({
  token,
  applicantId,
  applicantName,
  currentScores = null,
  currentTotal = 0,
  onClose,
  onSaved,
}) {
  const storageKey = `${STORAGE_PREFIX}${applicantId}`;

  const loadDraft = () => {
    // First, try to use currentScores from database if available
    if (currentScores) {
      return {
        resumeReview: currentScores.resumeReview != null ? String(currentScores.resumeReview) : "0",
        technicalTest: currentScores.technicalTest != null ? String(currentScores.technicalTest) : "0",
        technicalInterview: currentScores.technicalInterview != null ? String(currentScores.technicalInterview) : "0",
        hrRound: currentScores.hrRound != null ? String(currentScores.hrRound) : "0",
      };
    }
    
    // Fallback to localStorage draft only if no current scores
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw)
        return {
          resumeReview: "0",
          technicalTest: "0",
          technicalInterview: "0",
          hrRound: "0",
        };
      const parsed = JSON.parse(raw);
      return {
        resumeReview:
          parsed.resumeReview != null ? String(parsed.resumeReview) : "0",
        technicalTest:
          parsed.technicalTest != null ? String(parsed.technicalTest) : "0",
        technicalInterview:
          parsed.technicalInterview != null
            ? String(parsed.technicalInterview)
            : "0",
        hrRound: parsed.hrRound != null ? String(parsed.hrRound) : "0",
      };
    } catch {
      return {
        resumeReview: "0",
        technicalTest: "0",
        technicalInterview: "0",
        hrRound: "0",
      };
    }
  };

  const draft = loadDraft();
  const [resumeReview, setResumeReview] = useState(draft.resumeReview);
  const [technicalTest, setTechnicalTest] = useState(draft.technicalTest);
  const [technicalInterview, setTechnicalInterview] = useState(
    draft.technicalInterview
  );
  const [hrRound, setHrRound] = useState(draft.hrRound);
  const [loading, setLoading] = useState(false);

  const numeric = (v) => {
    if (v === "" || v == null) return 0;
    const n = Number(v);
    return Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
  };

  const newTotal =
    numeric(resumeReview) +
    numeric(technicalTest) +
    numeric(technicalInterview) +
    numeric(hrRound);

  const currentDerivedTotal = currentScores
    ? Number(currentScores.resumeReview ?? 0) +
      Number(currentScores.technicalTest ?? 0) +
      Number(currentScores.technicalInterview ?? 0) +
      Number(currentScores.hrRound ?? 0)
    : Number(currentTotal ?? 0);

  useEffect(() => {
    try {
      const payload = {
        resumeReview,
        technicalTest,
        technicalInterview,
        hrRound,
        updatedAt: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {}
  }, [resumeReview, technicalTest, technicalInterview, hrRound, storageKey]);

  const handleChange = (setter) => (val) => {
    if (val === "" || val == null) {
      setter("");
      return;
    }
    let n = Number(val);
    if (Number.isNaN(n)) n = 0;
    if (n < 0) n = 0;
    if (n > 100) n = 100;
    setter(String(n));
  };

  const handleSave = async () => {
    if (!token) return alert("Login required");
    setLoading(true);
    try {
      const payloadScores = {
        resumeReview: numeric(resumeReview),
        technicalTest: numeric(technicalTest),
        technicalInterview: numeric(technicalInterview),
        hrRound: numeric(hrRound),
      };

      const res = await updateApplicant(token, applicantId, {
        scores: payloadScores,
      });

      try {
        localStorage.removeItem(storageKey);
      } catch {}
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => onClose?.();

  const handleReset = () => {
    setResumeReview("");
    setTechnicalTest("");
    setTechnicalInterview("");
    setHrRound("");
  };

  return (
    <div className="score-backdrop">
      <div
        className="score-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-editor-title"
      >
        <header className="score-header">
          <div>
            <h3 id="score-editor-title">Update Scores</h3>
            <p className="score-subtitle">
              Applicant: <strong>{applicantName ?? applicantId}</strong>
            </p>
          </div>
          <button
            type="button"
            className="tiny-btn outline score-close-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            ✕
          </button>
        </header>

        <div className="score-row">
          <div className="score-card score-card--current">
            <div className="score-card-label">Current Score</div>
            <div className="score-card-value">{currentDerivedTotal}</div>
          </div>
          <div className="score-card score-card--new">
            <div className="score-card-label">New Score (live)</div>
            <div className="score-card-value">{newTotal}</div>
          </div>
        </div>

        <div className="score-grid">
          <EditableInput
            label="Resume Review"
            value={resumeReview}
            onChange={handleChange(setResumeReview)}
          />
          <EditableInput
            label="Technical Test"
            value={technicalTest}
            onChange={handleChange(setTechnicalTest)}
          />
          <EditableInput
            label="Technical Interview"
            value={technicalInterview}
            onChange={handleChange(setTechnicalInterview)}
          />
          <EditableInput
            label="HR Round"
            value={hrRound}
            onChange={handleChange(setHrRound)}
          />
        </div>

        <footer className="score-footer">
          <div className="score-footer-left">
            <button
              type="button"
              className="tiny-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tiny-btn"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
          <div className="score-footer-right">
            {/* Save button: larger, primary styling */}
            <button
              type="button"
              className="score-save-btn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function EditableInput({ label, value, onChange }) {
  return (
    <label className="score-input-group">
      <div className="score-input-label">{label}</div>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        placeholder="0"
        onChange={(e) => onChange(e.target.value.trim())}
        className="score-input"
      />
      <div className="score-input-hint">
        0–100, leave empty to remove default
      </div>
    </label>
  );
}
